import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, ShoppingCart } from "lucide-react";
import { CubeIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";
import { formatCurrency } from "@/utils/formatCurrency";
import { getProductTypeBadgeConfig } from "../../utils/productTypeBadge";
import { Button } from "@/components/ui/button";
import { useProductDetailPage } from "../../hooks/useProductDetailPage";

export default function ProductDetailPage() {
  const { product, loading, addingToCart, handleBuyNow, handleAddToCart } =
    useProductDetailPage();

  if (loading) {
    return (
      <div className="relative min-h-full">
        <Helmet>
          <title>Product Details</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="h-5 bg-gray-200 dark:bg-slate-600 rounded w-32 animate-pulse" />
          </div>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg" />
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 dark:bg-slate-600 rounded w-3/4" />
                <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-1/4" />
                <div className="h-5 bg-gray-200 dark:bg-slate-600 rounded w-1/3" />
                <div className="h-3 bg-gray-200 dark:bg-slate-600 rounded w-full" />
                <div className="h-3 bg-gray-200 dark:bg-slate-600 rounded w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="relative min-h-full">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <p className="text-gray-500 dark:text-gray-400">Product not found.</p>
        </div>
      </div>
    );
  }

  const badgeConfig = getProductTypeBadgeConfig(product.type);
  const BadgeIcon = badgeConfig.icon;

  return (
    <div className="relative min-h-full">
      <Helmet>
        <title>{product.name}</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <Link
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-6"
          to="/product-directory"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Link>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {product.thumbnailUrl ? (
              <img
                alt={product.name}
                className="object-cover w-full h-full rounded-lg aspect-video"
                src={product.thumbnailUrl}
              />
            ) : (
              <div className="aspect-video rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                <div className="flex flex-col items-center gap-2">
                  <CubeIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    No Image
                  </span>
                </div>
              </div>
            )}

            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {product.name}
              </h1>

              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${badgeConfig.style}`}
                >
                  <BadgeIcon className="h-3 w-3" />
                  {badgeConfig.label}
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <Eye className="h-4 w-4" />
                  {product.viewCount || 0} views
                </span>
              </div>

              <p className="text-xl font-medium text-primary mb-4">
                {formatCurrency(product.price)}
              </p>

              {product.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                  {product.description}
                </p>
              )}

              <div className="flex items-center gap-3 mt-6">
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  disabled={addingToCart}
                  onClick={handleAddToCart}
                  type="button"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {addingToCart ? "Adding..." : "Add to Cart"}
                </button>
                <Button
                  disabled={addingToCart}
                  onClick={handleBuyNow}
                  type="button"
                >
                  {addingToCart ? "Processing..." : "Buy Now"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
